import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Card,
  Tooltip,
  CardContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Chip,
  Button,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  CircularProgress,
  Alert,
  Autocomplete,
  Collapse } from
'@mui/material';
import { useIsMobile } from '../../hooks/useResponsive';
import {
  Search as SearchIcon,
  LocationOn as LocationIcon,
  Devices as DevicesIcon,
  History as HistoryIcon,
  Settings as SettingsIcon,
  Dashboard as DashboardIcon,
  AccountTree as TreeIcon,
  Hub as NetworkIcon,
  BugReport as TicketIcon,
  ArrowForward as NavigateIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  RadioButtonChecked as EnOceanButtonIcon,
  Warning as WarningIcon,
  Person as PersonIcon,
  LocalHospital as AlarmIcon,
  PlayCircle as TestIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Home as HomeIcon,
  People as FamilyIcon,
  Event as EventIcon,
  PhotoLibrary as PhotoLibraryIcon } from
'@mui/icons-material';
import HierarchicalLocationView from '../../components/LocationTree/HierarchicalLocationView';
import VisualTreeView from '../../components/LocationTree/VisualTreeView';
import LocationDialog from '../../components/LocationDialog';
import DeviceDialog from '../../components/DeviceDialog';
import ConfirmDialog from '../../components/ConfirmDialog';
import DeviceTable from '../../components/DeviceTable';
import CustomDataPanel from '../../components/CustomDataPanel';
import TicketsTable from '../../components/TicketsTable';
import LogsTable from '../../components/LogsTable';
import AlarmsTable from '../../components/AlarmsTable';
import ButtonTestDialog from '../../components/EnOcean/ButtonTestDialog';
import { QbusControlPanel } from '../../components/Qbus';
import FamilyLinksTab from '../../components/Locations/FamilyLinksTab';
import LocationEventsTab from '../../components/Locations/LocationEventsTab';
import PhotoModerationTab from '../../components/Locations/PhotoModerationTab';
import ResponsiveTabsAccordion from '../../components/ResponsiveTabsAccordion';
import { usePermissions } from '../../contexts/PermissionContext';
import { useSiteFilter } from '../../contexts/SiteFilterContext';
import LocationService from '../../services/locationService';
import { devicesApi, personsApi, deviceTypesApi, qbusApi } from '../../services/apiClient';
import enoceanService from '../../services/enoceanService';
import { useThemeSettings } from "../../contexts/ThemeContext";
import { ContentSkeleton } from '../../components/SkeletonLoaders';
import EmptyState from '../../components/EmptyState';
import ErrorMessage from '../../components/ErrorMessage';
import LabelManager from '../../components/LocationSearch/LabelManager';

const LocationManagementEnhanced = () => {const { t } = useThemeSettings();
  const { selectedSiteId, siteLocationIds, hasSiteFilter } = useSiteFilter();
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [parentLocation, setParentLocation] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [viewMode, setViewMode] = useState('tree');
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [locationsError, setLocationsError] = useState(null);
  const [totalLocations, setTotalLocations] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(1000);
  const [deviceDialogOpen, setDeviceDialogOpen] = useState(false);
  const [deviceDialogMode, setDeviceDialogMode] = useState('create');
  const [deviceToEdit, setDeviceToEdit] = useState(null);
  const [deleteDeviceConfirmOpen, setDeleteDeviceConfirmOpen] = useState(false);
  const [deviceToDelete, setDeviceToDelete] = useState(null);
  const [enoceanButtons, setEnoceanButtons] = useState([]);
  const [loadingButtons, setLoadingButtons] = useState(false);
  const [buttonError, setButtonError] = useState(null);
  const [buttonTestDialogOpen, setButtonTestDialogOpen] = useState(false);
  const [allDeviceTypes, setAllDeviceTypes] = useState([]);
  const [loadingDeviceTypes, setLoadingDeviceTypes] = useState(false);
  const [expectedDevicesExpanded, setExpectedDevicesExpanded] = useState(true);
  const [savingExpectedDevices, setSavingExpectedDevices] = useState(false);
  const [locationPersons, setLocationPersons] = useState([]);
  const [loadingPersons, setLoadingPersons] = useState(false);
  const { hasPermission } = usePermissions();
  const isMobile = useIsMobile();

  // Ref for the right panel to enable auto-scroll
  const locationDetailsPanelRef = React.useRef(null);

  // Check URL parameters for selected location
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const selectedId = urlParams.get('selectedId');

    if (selectedId && locations.length > 0) {
      // Find the location with the matching ID
      const findLocationById = (locs, id) => {
        for (const loc of locs) {
          if ((loc.id || loc._id || loc.location_UUID) === parseInt(id) ||
          (loc.id || loc._id || loc.location_UUID) === id) {
            return loc;
          }
          if (loc.children && loc.children.length > 0) {
            const found = findLocationById(loc.children, id);
            if (found) return found;
          }
        }
        return null;
      };

      const location = findLocationById(locations, selectedId);
      if (location) {
        setSelectedLocation(location);
        //console.log('Auto-selected location from URL:', location);

        // Optionally switch to devices tab to show devices for this location
        setSelectedTab(1); // Switch to devices tab
      }
    }
  }, [locations]);

  useEffect(() => {
    loadLocations();
  }, [refreshTrigger, selectedSiteId, siteLocationIds, hasSiteFilter]);

  // Load device types once on mount
  useEffect(() => {
    loadDeviceTypes();
  }, []);

  const loadDeviceTypes = async () => {
    setLoadingDeviceTypes(true);
    try {
      const response = await deviceTypesApi.getAll({ limit: 100 });
      const types = response?.data?.data || response?.data || [];
      setAllDeviceTypes(Array.isArray(types) ? types : []);
    } catch (error) {
      console.error('Error loading device types:', error);
      setAllDeviceTypes([]);
    } finally {
      setLoadingDeviceTypes(false);
    }
  };

  // Auto-scroll details panel when location changes
  useEffect(() => {
    if (selectedLocation && locationDetailsPanelRef.current && !isMobile) {
      locationDetailsPanelRef.current.scrollTo({
        top: 0,
        behavior: 'auto'
      });
    }
  }, [selectedLocation, isMobile]);

  const buildLocationHierarchy = (flatLocations) => {
    if (!Array.isArray(flatLocations) || flatLocations.length === 0) return [];

    // Create a map for quick lookup
    const locationMap = {};
    flatLocations.forEach((loc) => {
      const locId = loc.id || loc._id || loc.location_UUID;
      locationMap[locId] = { ...loc, children: [] };
    });

    // Build the tree structure
    const roots = [];
    flatLocations.forEach((loc) => {
      const locId = loc.id || loc._id || loc.location_UUID;
      const parentId = loc.parentLocation || loc.parent_id || loc.parentId;

      if (parentId && locationMap[parentId]) {
        // Add as child to parent
        locationMap[parentId].children.push(locationMap[locId]);
      } else {
        // No parent or parent not found, add as root
        roots.push(locationMap[locId]);
      }
    });

    return roots;
  };

  const loadLocations = async () => {
    try {
      setLoadingLocations(true);
      setLocationsError(null);

      // Wait for site filter to be ready if active
      // If hasSiteFilter is true but siteLocationIds is not yet loaded, don't fetch yet
      if (hasSiteFilter && !Array.isArray(siteLocationIds)) {
        console.log('[LocationManagement] Site filter active but siteLocationIds not loaded yet, waiting...');
        setLoadingLocations(false);
        return; // Will re-trigger when siteLocationIds becomes available
      }

      // Use static method from LocationService
      const response = await LocationService.getAllLocations({ useCache: !hasSiteFilter, forceRefresh: hasSiteFilter });
      console.log('[LocationManagement] API response:', response);

      // Extract locations from response
      let flatLocations = [];
      let total = 0;

      if (response?.data?.data && Array.isArray(response.data.data)) {
        flatLocations = response.data.data;
        total = response.data.total || flatLocations.length;
      } else if (response?.data?.locations && Array.isArray(response.data.locations)) {
        flatLocations = response.data.locations;
        total = response.data.total || flatLocations.length;
      } else if (response?.data?.docs && Array.isArray(response.data.docs)) {
        flatLocations = response.data.docs;
        total = response.data.totalDocs || response.data.total || flatLocations.length;
      } else if (response?.data && Array.isArray(response.data)) {
        flatLocations = response.data;
        total = flatLocations.length;
      } else if (Array.isArray(response)) {
        flatLocations = response;
        total = flatLocations.length;
      }

      // Filter by site if a site is selected
      // siteLocationIds should be an array at this point (checked above)
      console.log(`[LocationManagement] Site filter check - hasSiteFilter: ${hasSiteFilter}, siteLocationIds: ${siteLocationIds ? siteLocationIds.length : 'null'}, selectedSiteId: ${selectedSiteId}`);
      if (hasSiteFilter && Array.isArray(siteLocationIds)) {
        const beforeCount = flatLocations.length;
        flatLocations = flatLocations.filter(loc => {
          const locId = loc.id || loc._id || loc.location_UUID;
          const isIncluded = siteLocationIds.includes(Number(locId)) || siteLocationIds.includes(String(locId));
          return isIncluded;
        });
        total = flatLocations.length;
        console.log(`[LocationManagement] Filtered from ${beforeCount} to ${flatLocations.length} locations for selected site (siteLocationIds count: ${siteLocationIds.length})`);
      }

      console.log(`[LocationManagement] Loaded ${flatLocations.length} of ${total} locations`);

      // Build hierarchical structure for VisualTreeView
      const hierarchicalLocations = buildLocationHierarchy(flatLocations);

      console.log('[LocationManagement] Hierarchical locations:', hierarchicalLocations.length);
      setLocations(hierarchicalLocations);
      setTotalLocations(total);
    } catch (error) {
      console.error('[LocationManagement] Error loading locations:', error);
      setLocationsError(error);
      setLocations([]);
      setTotalLocations(0);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Handle location selection
  const handleLocationSelect = async (location) => {
    setSelectedLocation(location);

    // Scroll details panel to top immediately
    if (locationDetailsPanelRef.current && !isMobile) {
      // Use setTimeout to ensure DOM is updated
      setTimeout(() => {
        if (locationDetailsPanelRef.current) {
          locationDetailsPanelRef.current.scrollTo({
            top: 0,
            behavior: 'auto' // Instant scroll instead of smooth
          });
        }
      }, 0);
    }

    // Load devices for this location
    if (location) {
      setLoadingDevices(true);
      setLoadingPersons(true);
      try {
        const deviceResponse = await LocationService.getLocationDevices(location.id || location._id);
        const deviceData = deviceResponse.data;
        setDevices(deviceData);
      } catch (error) {
        console.error('Error loading devices:', error);
        setDevices([]);
      } finally {
        setLoadingDevices(false);
      }

      // Load persons for this location
      try {
        const personsResponse = await personsApi.getByLocation(location.id || location._id);
        const personsData = personsResponse.data?.data || personsResponse.data || [];
        setLocationPersons(personsData);
      } catch (error) {
        console.error('Error loading persons:', error);
        setLocationPersons([]);
      } finally {
        setLoadingPersons(false);
      }
    }
  };

  // Load EnOcean buttons for the selected location
  const loadEnoceanButtons = useCallback(async (locationId) => {
    if (!locationId) {
      setEnoceanButtons([]);
      return;
    }

    setLoadingButtons(true);
    setButtonError(null);

    try {
      const response = await enoceanService.getButtons({ locationId });
      if (response.success) {
        setEnoceanButtons(response.data || []);
      } else {
        setEnoceanButtons([]);
      }
    } catch (error) {
      console.error('Error loading EnOcean buttons:', error);
      setButtonError('Failed to load EnOcean buttons');
      setEnoceanButtons([]);
    } finally {
      setLoadingButtons(false);
    }
  }, []);

  // Load buttons when location changes
  useEffect(() => {
    if (selectedLocation) {
      loadEnoceanButtons(selectedLocation.id || selectedLocation._id);
    } else {
      setEnoceanButtons([]);
    }
  }, [selectedLocation, loadEnoceanButtons]);

  // Handle delete EnOcean button
  const handleDeleteButton = async (button) => {
    if (!window.confirm(`Are you sure you want to delete button "${button.friendlyName || button.hexCode}"?`)) {
      return;
    }

    try {
      await enoceanService.deleteButton(button.id);
      // Refresh buttons
      if (selectedLocation) {
        loadEnoceanButtons(selectedLocation.id || selectedLocation._id);
      }
    } catch (error) {
      console.error('Error deleting button:', error);
      setButtonError('Failed to delete button');
    }
  };

  const handleAddDevice = () => {
    setDeviceToEdit(null);
    setDeviceDialogMode('create');
    setDeviceDialogOpen(true);
  };

  const handleEditDevice = (device) => {
    setDeviceToEdit(device);
    setDeviceDialogMode('edit');
    setDeviceDialogOpen(true);
  };

  const handleDeleteDevice = (device) => {
    setDeviceToDelete(device);
    setDeleteDeviceConfirmOpen(true);
  };

  const handleDeviceSave = async () => {
    setDeviceDialogOpen(false);
    // Refresh devices
    if (selectedLocation) {
      await handleLocationSelect(selectedLocation);
    }
  };

  const handleConfirmDeviceDelete = async () => {
    if (deviceToDelete) {
      try {
        const deviceId = deviceToDelete.id || deviceToDelete._id || deviceToDelete.device_UUID;
        await devicesApi.delete(deviceId);
        setDeleteDeviceConfirmOpen(false);
        setDeviceToDelete(null);
        // Refresh devices
        if (selectedLocation) {
          await handleLocationSelect(selectedLocation);
        }
      } catch (error) {
        console.error('Error deleting device:', error);
      }
    }
  };

  // Handle add location
  const handleAddLocation = (parent) => {
    setParentLocation(parent);
    setDialogMode('create');
    setDialogOpen(true);
  };

  // Handle edit location
  const handleEditLocation = (location) => {
    setSelectedLocation(location);
    setDialogMode('edit');
    setDialogOpen(true);
  };

  // Handle delete location
  const handleDeleteLocation = (location) => {
    setLocationToDelete(location);
    setConfirmDialogOpen(true);
  };

  // Confirm delete
  const confirmDelete = async () => {
    if (!locationToDelete) return;

    try {
      await LocationService.deleteLocation(locationToDelete.id || locationToDelete._id);
      setRefreshTrigger((prev) => prev + 1);
      setConfirmDialogOpen(false);
      setLocationToDelete(null);
      if (selectedLocation?.id === locationToDelete.id) {
        setSelectedLocation(null);
      }
    } catch (error) {
      console.error('Error deleting location:', error);
    }
  };

  // Handle save from dialog
  const handleSave = () => {
    setRefreshTrigger((prev) => prev + 1);
    setDialogOpen(false);
  };

  // Get expected device types from location data
  // Supports both old format (array of IDs) and new format (array of {deviceTypeId, minCount})
  const expectedDeviceTypes = useMemo(() => {
    if (!selectedLocation?.data || !Array.isArray(selectedLocation.data)) return [];
    const entry = selectedLocation.data.find(d => d.name === 'requiredDeviceTypes');
    if (!entry?.value) return [];

    // Convert to new format if needed
    return entry.value.map(item => {
      if (typeof item === 'number') {
        // Old format: just an ID, default minCount to 1
        return { deviceTypeId: item, minCount: 1 };
      }
      // New format: { deviceTypeId, minCount }
      return item;
    });
  }, [selectedLocation?.data]);

  // Compute expected devices status (fulfilled vs missing)
  const expectedDevicesStatus = useMemo(() => {
    if (!expectedDeviceTypes.length) return [];

    return expectedDeviceTypes.map(expected => {
      const deviceType = allDeviceTypes.find(dt => dt.id === expected.deviceTypeId);
      const devicesOfType = devices.filter(d =>
        (d.deviceTypeModel?.id || d.deviceType) === expected.deviceTypeId
      );
      const actualCount = devicesOfType.length;
      const minCount = expected.minCount || 1;

      return {
        id: expected.deviceTypeId,
        name: deviceType?.FrontEndName || deviceType?.friendlyName || `Type ${expected.deviceTypeId}`,
        minCount: minCount,
        actualCount: actualCount,
        fulfilled: actualCount >= minCount,
        devices: devicesOfType
      };
    });
  }, [expectedDeviceTypes, devices, allDeviceTypes]);

  // Save expected device types to location data
  const saveExpectedDeviceTypes = async (newExpectedTypes) => {
    if (!selectedLocation) return;

    setSavingExpectedDevices(true);
    try {
      const locationId = selectedLocation.id || selectedLocation._id;

      // Get existing data array or create new one
      let existingData = Array.isArray(selectedLocation.data) ? [...selectedLocation.data] : [];

      // Remove old requiredDeviceTypes entry if exists
      existingData = existingData.filter(d => d.name !== 'requiredDeviceTypes');

      // Add new entry if there are expected types
      if (newExpectedTypes.length > 0) {
        existingData.push({
          name: 'requiredDeviceTypes',
          type: 'deviceTypeArray',
          value: newExpectedTypes,
          visible: true,
          allowedUsers: [],
          sendWithAlarm: false,
          permissionLevel: 'admin'
        });
      }

      await LocationService.updateLocation(locationId, { data: existingData });

      // Update local state
      const updatedLocation = { ...selectedLocation, data: existingData };
      setSelectedLocation(updatedLocation);

      // Update in the locations array
      setLocations((prevLocations) => {
        const updateNode = (nodes) => {
          return nodes.map((node) => {
            if ((node.id || node._id) === locationId) {
              return { ...node, data: existingData };
            }
            if (node.children) {
              return { ...node, children: updateNode(node.children) };
            }
            return node;
          });
        };
        return updateNode(prevLocations);
      });
    } catch (error) {
      console.error('Error saving expected device types:', error);
    } finally {
      setSavingExpectedDevices(false);
    }
  };

  // Add expected device type
  const handleAddExpectedDeviceType = (deviceType) => {
    if (!deviceType || expectedDeviceTypes.some(e => e.deviceTypeId === deviceType.id)) return;
    saveExpectedDeviceTypes([...expectedDeviceTypes, { deviceTypeId: deviceType.id, minCount: 1 }]);
  };

  // Remove expected device type
  const handleRemoveExpectedDeviceType = (typeId) => {
    saveExpectedDeviceTypes(expectedDeviceTypes.filter(e => e.deviceTypeId !== typeId));
  };

  // Update minimum count for an expected device type
  const handleUpdateMinCount = (typeId, newMinCount) => {
    const count = Math.max(1, parseInt(newMinCount) || 1);
    saveExpectedDeviceTypes(
      expectedDeviceTypes.map(e =>
        e.deviceTypeId === typeId ? { ...e, minCount: count } : e
      )
    );
  };

  // Define tabs for ResponsiveTabsAccordion
  const locationTabs = selectedLocation ? [
  {
    label: 'Overview',
    icon: <DashboardIcon />,
    content:
    <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>{t ? t("LocationDetails.location_information") : "Location Information"}

            </Typography>
                <List dense>
                  {selectedLocation.parentLocation &&
              <ListItem
                button
                onClick={() => {
                  // Find parent location in the tree and select it
                  const findLocation = (nodes, id) => {
                    for (const node of nodes) {
                      if ((node.id || node._id) === id) return node;
                      if (node.children) {
                        const found = findLocation(node.children, id);
                        if (found) return found;
                      }
                    }
                    return null;
                  };
                  const parent = findLocation(locations, selectedLocation.parentLocation);
                  if (parent) handleLocationSelect(parent);
                }}
                sx={{
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    cursor: 'pointer'
                  }
                }}>
                
                      <ListItemIcon>
                        <LocationIcon />
                      </ListItemIcon>
                      <ListItemText
                  primary="Parent Location"
                  secondary={selectedLocation.parentLocationName || 'Parent'} />
                
                      <Tooltip title={t ? t("LocationManagement.navigate_parent_location") : "Navigate to parent location"}>
                        <IconButton size="small" edge="end">
                          <NavigateIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItem>
              }
                  <ListItem>
                    <ListItemText
                  primary="Type"
                  secondary={selectedLocation.locationTypeModel.friendlyName} />
                
                  </ListItem>
                  <ListItem>
                    <ListItemText
                  primary="Created"
                  secondary={new Date(selectedLocation.createdAt || selectedLocation.created).toLocaleDateString()} />
                
                  </ListItem>
                  {selectedLocation.updatedAt &&
              <ListItem>
                      <ListItemText
                  primary="Last Updated"
                  secondary={new Date(selectedLocation.updatedAt).toLocaleDateString()} />
                
                    </ListItem>
              }
                </List>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>{t ? t("LocationDetails.statistics") : "Statistics"}

            </Typography>
                <List dense>
                  <ListItem>
                    <ListItemIcon>
                      <DevicesIcon />
                    </ListItemIcon>
                    <ListItemText
                  primary="Devices"
                  secondary={`${devices.length} devices in this location`} />
                
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <LocationIcon />
                    </ListItemIcon>
                    <ListItemText
                  primary="Sublocations"
                  secondary={`${selectedLocation.children?.length || 0} direct sublocations`} />
                
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>

          {selectedLocation.address &&
      <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>{t ? t("LocationManagement.address") : "Address"}

            </Typography>
                  <Typography variant="body2" sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}>
                    {selectedLocation.address.street && `${selectedLocation.address.street}, `}
                    {selectedLocation.address.city && `${selectedLocation.address.city}, `}
                    {selectedLocation.address.state && `${selectedLocation.address.state} `}
                    {selectedLocation.address.postalCode}
                    {selectedLocation.address.country && `, ${selectedLocation.address.country}`}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
      }

          {/* Custom Data Panel */}
          <Grid item xs={12}>
            <CustomDataPanel
          title={t ? t("VisualTreeView.custom_location_data") : "Custom Location Data"}
          data={selectedLocation.data || []}
          entityType="location"
          entityId={selectedLocation.id || selectedLocation._id}
          onSave={async (newData) => {
            try {
              const locationId = selectedLocation.id || selectedLocation._id;
              await LocationService.updateLocation(
                locationId,
                { data: newData }
              );

              // Update the selected location with new data
              const updatedLocation = {
                ...selectedLocation,
                data: newData
              };

              // Update local state
              setSelectedLocation(updatedLocation);

              // Update in the locations array
              setLocations((prevLocations) => {
                const updateNode = (nodes) => {
                  return nodes.map((node) => {
                    if ((node.id || node._id) === locationId) {
                      return { ...node, data: newData };
                    }
                    if (node.children) {
                      return { ...node, children: updateNode(node.children) };
                    }
                    return node;
                  });
                };
                return updateNode(prevLocations);
              });

              return true; // Success indicator
            } catch (error) {
              console.error('Error updating location data:', error);
              throw error; // Re-throw to let CustomDataPanel handle it
            }
          }} />
        
          </Grid>
        </Grid>

  },
  {
    label: 'Devices',
    icon: <DevicesIcon />,
    content:
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
          {/* Expected Devices Section - Only for Unit locations (type 4) */}
          {selectedLocation.locationType === 4 && (
            <Card sx={{ mb: 2 }}>
              <CardContent sx={{ pb: 1 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer'
                  }}
                  onClick={() => setExpectedDevicesExpanded(!expectedDevicesExpanded)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <DevicesIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Expected Devices
                    </Typography>
                    {expectedDevicesStatus.length > 0 && (
                      <Chip
                        size="small"
                        label={`${expectedDevicesStatus.filter(e => e.fulfilled).length}/${expectedDevicesStatus.length}`}
                        color={expectedDevicesStatus.every(e => e.fulfilled) ? 'success' : 'warning'}
                      />
                    )}
                    {savingExpectedDevices && <CircularProgress size={16} sx={{ ml: 1 }} />}
                  </Box>
                  <IconButton size="small">
                    {expectedDevicesExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Collapse in={expectedDevicesExpanded}>
                  <Box sx={{ mt: 2 }}>
                    {/* Add new expected device type */}
                    <Autocomplete
                      size="small"
                      options={allDeviceTypes.filter(dt => !expectedDeviceTypes.some(e => e.deviceTypeId === dt.id))}
                      getOptionLabel={(option) => option.FrontEndName || option.friendlyName || `Type ${option.id}`}
                      loading={loadingDeviceTypes}
                      disabled={savingExpectedDevices}
                      onChange={(event, newValue) => {
                        if (newValue) {
                          handleAddExpectedDeviceType(newValue);
                        }
                      }}
                      value={null}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Add expected device type"
                          placeholder="Select a device type to add"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {loadingDeviceTypes ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                      sx={{ mb: 2 }}
                    />

                    {/* Expected devices list */}
                    {expectedDevicesStatus.length > 0 ? (
                      <List dense sx={{ bgcolor: 'action.hover', borderRadius: 1, p: 1 }}>
                        {expectedDevicesStatus.map((expected) => (
                          <ListItem
                            key={expected.id}
                            sx={{
                              borderLeft: 4,
                              borderColor: expected.fulfilled ? 'success.main' : 'error.main',
                              mb: 0.5,
                              bgcolor: 'background.paper',
                              borderRadius: '0 4px 4px 0',
                              pr: 12
                            }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              {expected.fulfilled ? (
                                <CheckCircleIcon color="success" />
                              ) : (
                                <CancelIcon color="error" />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={expected.name}
                              secondary={
                                expected.fulfilled
                                  ? `${expected.actualCount}/${expected.minCount} present`
                                  : `${expected.actualCount}/${expected.minCount} - Need ${expected.minCount - expected.actualCount} more`
                              }
                              primaryTypographyProps={{ fontWeight: 500 }}
                            />
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'absolute', right: 48 }}>
                              <Typography variant="caption" color="text.secondary">
                                Min:
                              </Typography>
                              <TextField
                                type="number"
                                size="small"
                                value={expected.minCount}
                                onChange={(e) => handleUpdateMinCount(expected.id, e.target.value)}
                                disabled={savingExpectedDevices}
                                inputProps={{ min: 1, style: { width: 40, textAlign: 'center', padding: '4px' } }}
                                sx={{ width: 60 }}
                              />
                            </Box>
                            <ListItemSecondaryAction>
                              <Tooltip title="Remove from expected">
                                <IconButton
                                  edge="end"
                                  size="small"
                                  onClick={() => handleRemoveExpectedDeviceType(expected.id)}
                                  disabled={savingExpectedDevices}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                        No expected device types defined. Add device types above to track what should be present in this unit.
                      </Typography>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
            </Card>
          )}

          <DeviceTable
        devices={devices}
        loading={loadingDevices}
        locationId={selectedLocation.id || selectedLocation._id}
        onRefresh={() => handleLocationSelect(selectedLocation)}
        onEditDevice={handleEditDevice}
        onDeleteDevice={handleDeleteDevice}
        onAddDevice={handleAddDevice} />

        </Box>

  },
  {
    label: 'Tickets',
    icon: <TicketIcon />,
    content:
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <TicketsTable
        locationId={selectedLocation?.id || selectedLocation?._id || selectedLocation?.location_UUID}
        locationData={selectedLocation}
        onViewTicket={(ticket) => {
          const ticketId = ticket.id || ticket._id;
          window.location.href = `/tickets?selectedId=${ticketId}`;
        }} />

        </Box>

  },
  {
    label: 'Alarms',
    icon: <AlarmIcon />,
    content:
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <AlarmsTable
        locationId={selectedLocation?.id || selectedLocation?._id}
        onViewAlarm={(alarm) => {
          window.location.href = `/alarms?selectedId=${alarm.id}`;
        }} />

        </Box>

  },
  {
    label: 'History',
    icon: <HistoryIcon />,
    content:
    <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <LogsTable
        locationId={selectedLocation?.id || selectedLocation?._id || selectedLocation?.location_UUID} />

        </Box>

  },
  {
    label: 'Persons',
    icon: <PersonIcon />,
    content:
    <Box sx={{ width: '100%' }}>
          {loadingPersons ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : locationPersons.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <PersonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                No persons at this location
              </Typography>
              <Button
                variant="outlined"
                startIcon={<NavigateIcon />}
                onClick={() => window.location.href = `/persons?location=${selectedLocation?.id || selectedLocation?._id}`}
                sx={{ mt: 1 }}
              >
                Manage Persons
              </Button>
            </Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Persons ({locationPersons.length})
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<NavigateIcon />}
                  onClick={() => window.location.href = `/persons?location=${selectedLocation?.id || selectedLocation?._id}`}
                >
                  View All
                </Button>
              </Box>
              <List>
                {locationPersons.map((person) => (
                  <ListItem
                    key={person.id}
                    sx={{
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      mb: 1,
                      bgcolor: 'background.paper'
                    }}
                  >
                    <ListItemIcon>
                      <PersonIcon color={person.inviteStatus === 'accepted' ? 'success' : 'action'} />
                    </ListItemIcon>
                    <ListItemText
                      primary={person.friendlyName}
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                          {person.personTypeModel?.friendlyName && (
                            <Chip
                              size="small"
                              label={person.personTypeModel.friendlyName}
                              variant="outlined"
                            />
                          )}
                          {person.inviteStatus === 'accepted' && (
                            <Chip
                              size="small"
                              label="Active User"
                              color="success"
                              variant="outlined"
                            />
                          )}
                          {person.inviteStatus === 'pending' && (
                            <Chip
                              size="small"
                              label="Invite Pending"
                              color="warning"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Box>

  },
  {
    label: 'Settings',
    icon: <SettingsIcon />,
    content:
    <Box>
          {/* EnOcean Buttons Section */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EnOceanButtonIcon color="primary" />
                  <Typography variant="h6">
                    EnOcean Buttons
                  </Typography>
                  <Chip
                    size="small"
                    label={enoceanButtons.length}
                    color={enoceanButtons.length > 0 ? 'primary' : 'default'}
                  />
                </Box>
                {enoceanButtons.length > 0 && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<TestIcon />}
                    onClick={() => setButtonTestDialogOpen(true)}
                  >
                    Test Buttons
                  </Button>
                )}
              </Box>

              {buttonError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setButtonError(null)}>
                  {buttonError}
                </Alert>
              )}

              {loadingButtons ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                  <CircularProgress size={32} />
                </Box>
              ) : enoceanButtons.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3 }}>
                  <EnOceanButtonIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    No EnOcean buttons assigned to this location
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Use Button Discovery from a Hub device to register new buttons
                  </Typography>
                </Box>
              ) : (
                <List dense>
                  {enoceanButtons.map((button) => (
                    <ListItem
                      key={button.id}
                      sx={{
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                        mb: 1,
                        bgcolor: 'background.paper'
                      }}
                    >
                      <ListItemIcon>
                        <EnOceanButtonIcon
                          color={button.buttonType === 'emergency' ? 'error' : 'primary'}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle2">
                              {button.friendlyName || `Button ${button.hexCode}`}
                            </Typography>
                            <Chip
                              size="small"
                              label={button.buttonType || 'action'}
                              color={button.buttonType === 'emergency' ? 'error' : 'default'}
                              variant="outlined"
                            />
                          </Box>
                        }
                        secondary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                            <Typography variant="caption" fontFamily="monospace">
                              HEX: {button.hexCode}
                            </Typography>
                            {button.person && (
                              <Chip
                                size="small"
                                icon={<PersonIcon />}
                                label={button.person.friendlyName}
                                variant="outlined"
                              />
                            )}
                            <Chip
                              size="small"
                              label={button.status || 'unknown'}
                              color={button.status === 'online' ? 'success' : 'default'}
                              variant="outlined"
                            />
                          </Box>
                        }
                      />
                      <ListItemSecondaryAction>
                        <Tooltip title="Delete Button">
                          <IconButton
                            edge="end"
                            size="small"
                            color="error"
                            onClick={() => handleDeleteButton(button)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>

          {/* Other Settings */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t ? t("LocationManagement.location_settings") : "Location Settings"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t ? t("VisualTreeView.locationspecific_settings_permission") : "Location-specific settings and permissions will be displayed here"}
              </Typography>
            </CardContent>
          </Card>
        </Box>

  },
  {
    label: 'Family Links',
    icon: <FamilyIcon />,
    content:
    <FamilyLinksTab locationId={selectedLocation?.id || selectedLocation?._id} />
  },
  {
    label: 'Events',
    icon: <EventIcon />,
    content:
    <LocationEventsTab locationId={selectedLocation?.id || selectedLocation?._id} />
  },
  {
    label: 'Photos',
    icon: <PhotoLibraryIcon />,
    content:
    <PhotoModerationTab locationId={selectedLocation?.id || selectedLocation?._id} />
  },
  {
    label: t ? t("LocationDetails.domotics") : 'Domotics',
    icon: <HomeIcon />,
    content:
    <QbusControlPanel
      locationId={selectedLocation?.id || selectedLocation?._id}
      locationName={selectedLocation?.friendlyName || selectedLocation?.name}
    />
  }] :
  [];

  return (
    <Box
      className="page-transition-enter"
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}>

      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h4" className="slide-down" sx={{ fontWeight: 600 }}>{t ? t("LocationManagement.location_management") : "Location Management"}

        </Typography>
        <Typography variant="body2" color="text.secondary">{t ? t("LocationManagement.manage_your_location_hierarchy_assoc") : "Manage your location hierarchy and associated devices"}

        </Typography>
      </Box>

      {/* View Mode Toggle */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, newMode) => newMode && setViewMode(newMode)}
          size="small"
          sx={{ mb: 0 }}>
          
          <ToggleButton value="tree">
            <TreeIcon sx={{ mr: 1 }} />{t ? t("DeviceManagement.tree_view") : "Tree View"}

          </ToggleButton>
          <ToggleButton value="visual">
            <NetworkIcon sx={{ mr: 1 }} />{t ? t("DeviceManagement.network_view") : "Network View"}

          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Main Content */}
      {viewMode === 'tree' ?
      <Grid container sx={{
          flex: 1,
          overflow: 'hidden',
          height: isMobile ? 'auto' : 'calc(100vh - 180px)' // Full viewport minus header/navbar
        }}>
          {/* Left Panel - Location Tree */}
          <Grid item xs={12} md={4} sx={{
          borderRight: { xs: 0, md: 1 },
          borderBottom: { xs: 1, md: 0 },
          borderColor: 'divider',
          height: isMobile ? 'auto' : '100%',
          minHeight: isMobile ? '400px' : 'auto',
          maxHeight: isMobile ? '50vh' : '100%',
          overflowY: 'auto',
          overflowX: 'hidden'
        }}>
            <Box sx={{ p: 2 }}>
              <HierarchicalLocationView
              onLocationSelect={handleLocationSelect}
              onLocationEdit={handleEditLocation}
              onLocationDelete={handleDeleteLocation}
              onLocationAdd={handleAddLocation}
              selectedLocationId={selectedLocation?.id || selectedLocation?._id}
              refreshTrigger={refreshTrigger} />

            </Box>
          </Grid>

          {/* Right Panel - Location Details */}
          <Grid
            item
            xs={12}
            md={8}
            ref={locationDetailsPanelRef}
            sx={{
              height: isMobile ? 'auto' : '100%',
              overflowY: 'auto',
              overflowX: 'hidden'
            }}>
          {selectedLocation ?
          <Box sx={{ p: 3 }}>
              {/* Location Header */}
              <Paper sx={{ p: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                  {/* Left side - Location info */}
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                      <Box sx={{
                      p: 1.5,
                      borderRadius: 2,
                      bgcolor: 'primary.main',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                        <LocationIcon />
                      </Box>
                      <Box>
                        <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {selectedLocation.friendlyName}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip
                          label={selectedLocation.locationTypeModel?.friendlyName || 'Unknown Type'}
                          size="small"
                          variant="outlined" />
                        
                        </Box>
                      </Box>
                    </Box>
                    {selectedLocation.description &&
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 1 }}>
                        {selectedLocation.description}
                      </Typography>
                  }
                    {devices.length > 0 &&
                  <Chip
                    icon={<DevicesIcon />}
                    label={`${devices.length} Devices`}
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1 }} />

                  }

                    {/* Label Manager */}
                    <Box sx={{ mt: 2 }}>
                      <LabelManager
                        locationId={selectedLocation.id || selectedLocation._id}
                        locationName={selectedLocation.friendlyName}
                        onLabelsChange={() => setRefreshTrigger(prev => prev + 1)}
                      />
                    </Box>
                  </Box>

                  {/* Right side - Action buttons */}
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {hasPermission('locations.create') &&
                  <Tooltip title={t ? t("HierarchicalLocationView.add_sublocation_1") : "Add Sublocation"}>
                        <IconButton
                      onClick={() => handleAddLocation(selectedLocation)}
                      sx={{
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': { bgcolor: 'primary.dark' }
                      }}>
                      
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                  }

                    <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                    {hasPermission('locations.edit') &&
                  <Tooltip title={t ? t("LocationManagement.edit_location") : "Edit Location"}>
                        <IconButton
                      onClick={() => handleEditLocation(selectedLocation)}
                      sx={{
                        bgcolor: 'action.hover',
                        '&:hover': { bgcolor: 'action.selected' }
                      }}>
                      
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                  }

                    {hasPermission('locations.delete') &&
                  <Tooltip title={t ? t("LocationManagement.delete_location") : "Delete Location"}>
                        <IconButton
                      onClick={() => handleDeleteLocation(selectedLocation)}
                      sx={{
                        color: 'error.main',
                        bgcolor: 'error.lighter',
                        '&:hover': { bgcolor: 'error.light', color: 'white' }
                      }}>
                      
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                  }
                  </Box>
                </Box>
              </Paper>

              {/* Responsive Tabs/Accordion */}
              <ResponsiveTabsAccordion
              tabs={locationTabs}
              value={selectedTab}
              onChange={(e, v) => setSelectedTab(v)}
              defaultAllClosed={true} />
            
            </Box> :

          <Box sx={{ p: 3, textAlign: 'center' }}>
              <LocationIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h5" color="text.secondary" gutterBottom>{t ? t("LocationManagement.select_location") : "Select a Location"}

            </Typography>
              <Typography variant="body2" color="text.secondary">{t ? t("LocationManagement.choose_location_from_tree_view") : "Choose a location from the tree view to see details and manage devices"}

            </Typography>
            </Box>
          }
        </Grid>
      </Grid> : (

      /* Network View - Full Width */
      <Box sx={{ flex: 1, overflow: 'hidden', position: 'relative' }}>
          {loadingLocations ?
        <Box sx={{ p: 3 }}>
              <ContentSkeleton />
            </Box> :
        locationsError ?
        <Box sx={{ p: 3 }}>
              <ErrorMessage
            error={locationsError}
            title={t ? t("LocationManagement.error_loading_locations") : "Error Loading Locations"}
            context="locations"
            onRetry={loadLocations}
            showDetails={true} />

            </Box> :
        locations.length === 0 ?
        <Box sx={{ p: 3 }}>
              <EmptyState
            type="generic"
            title={t ? t("LocationManagement.no_locations") : "No Locations"}
            description={t ? t("LocationManagement.no_locations_desc") : "No locations have been created yet. Add your first location to get started."}
            icon={<LocationIcon sx={{ fontSize: 80, color: 'text.disabled' }} />}
            action={hasPermission('locations.create') ? {
              label: t ? t("LocationManagement.add_location") : "Add Location",
              onClick: () => handleAddLocation(null),
              icon: <AddIcon />
            } : null} />

            </Box> :
        <VisualTreeView
          locations={locations}
          selectedLocationId={selectedLocation?.id || selectedLocation?._id || selectedLocation?.location_UUID}
          onLocationSelect={handleLocationSelect}
          onLocationEdit={handleEditLocation}
          onLocationDelete={handleDeleteLocation}
          onLocationAdd={handleAddLocation}
          selectedLocation={selectedLocation} />

        }
        </Box>)
      }

      {/* Dialogs */}
      <LocationDialog
        open={dialogOpen}
        mode={dialogMode}
        location={dialogMode === 'edit' ? selectedLocation : null}
        parentLocation={parentLocation}
        onClose={() => setDialogOpen(false)}
        onSave={handleSave} />
      

      <ConfirmDialog
        open={confirmDialogOpen}
        title={t ? t("LocationManagement.delete_location") : "Delete Location"}
        content={`Are you sure you want to delete "${locationToDelete?.name}"? This action cannot be undone.`}
        confirmText={t ? t("DeviceTable.delete") : "Delete"}
        confirmColor="error"
        onConfirm={confirmDelete}
        onCancel={() => {
          setConfirmDialogOpen(false);
          setLocationToDelete(null);
        }} />
      

      <DeviceDialog
        open={deviceDialogOpen}
        mode={deviceDialogMode}
        device={deviceToEdit}
        onClose={() => setDeviceDialogOpen(false)}
        onSave={handleDeviceSave} />
      

      <ConfirmDialog
        open={deleteDeviceConfirmOpen}
        title={t ? t("DeviceTable.delete_device") : "Delete Device"}
        content={`Are you sure you want to delete "${deviceToDelete?.friendlyName || 'this device'}"? This action cannot be undone.`}
        confirmText={t ? t("DeviceTable.delete") : "Delete"}
        confirmColor="error"
        onConfirm={handleConfirmDeviceDelete}
        onCancel={() => setDeleteDeviceConfirmOpen(false)} />

      {/* Button Test Dialog */}
      <ButtonTestDialog
        open={buttonTestDialogOpen}
        onClose={() => setButtonTestDialogOpen(false)}
        locationId={selectedLocation?.id || selectedLocation?._id}
        locationName={selectedLocation?.friendlyName || selectedLocation?.name}
        onTestComplete={(result) => {
          // Refresh the buttons list after test
          if (selectedLocation) {
            handleLocationSelect(selectedLocation);
          }
        }}
      />

    </Box>);

};

export default LocationManagementEnhanced;